// ─────────────────────────────────────────────────────────────────────────────
// Sidebar.jsx  —  Left navigation drawer
//
// WHAT TO EDIT HERE:
//   Brand name / logo   → find "DISPATCHER" text and the SVG path below it
//   Sidebar color       → change background:"#1a2744" on <aside>
//   Active nav color    → change "bg-blue-600" on the active button
//   Nav items           → edit NAV_ITEMS in constants.js (not here)
//   Summary rows        → edit SUMMARY_CARDS in constants.js (not here)
//   CTA button label    → find "New Order" text near the bottom
//
// PROPS:
//   activeNav      {string}   current nav key
//   setActiveNav   {fn}       update active nav
//   sidebarOpen    {boolean}  mobile drawer open state
//   setSidebarOpen {fn}       toggle mobile drawer
// ─────────────────────────────────────────────────────────────────────────────

import { NAV_ITEMS, SUMMARY_CARDS } from "./constants";

export default function Sidebar({ activeNav, setActiveNav, sidebarOpen, setSidebarOpen }) {
  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full w-56 flex-shrink-0 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ background: "#1a2744" }}
      >
        {/* ── LOGO ROW ─────────────────────────────────────────────────────── */}
        {/* To change icon: swap the SVG path inside the blue circle          */}
        {/* To change brand name: change "DISPATCHER" text                    */}
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-white/10 flex-shrink-0">
          <div className="w-9 h-9 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 17H5a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3m-1 11a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2h8z"/>
            </svg>
          </div>
          <span className="font-bold text-white text-base tracking-wide">DISPATCHER</span>
          <button onClick={() => setSidebarOpen(false)} className="ml-auto lg:hidden text-white/50 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* ── NAV LINKS ─────────────────────────────────────────────────────── */}
        {/* Items come from NAV_ITEMS in constants.js                          */}
        {/* Active style: bg-blue-600  |  Hover style: bg-white/10            */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
          {NAV_ITEMS.map(({ key, label, icon, badge }) => (
            <button
              key={key}
              onClick={() => { setActiveNav(key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeNav === key
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                {icon.split(" M").map((seg, i) => (
                  <path key={i} strokeLinecap="round" strokeLinejoin="round" d={i === 0 ? seg : "M" + seg}/>
                ))}
              </svg>
              <span className="truncate">{label}</span>
              {badge && (
                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* ── TODAY SUMMARY BOX ─────────────────────────────────────────────── */}
        {/* Values come from SUMMARY_CARDS in constants.js                     */}
        <div
          className="mx-3 mb-2 rounded-xl p-3 border border-white/10 flex-shrink-0"
          style={{ background: "rgba(255,255,255,0.05)" }}
        >
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
            Today Summary
          </p>
          {SUMMARY_CARDS.slice(0, 4).map(c => (
            <div key={c.label} className="flex justify-between items-center py-0.5">
              <span className="text-xs text-gray-400">{c.label}</span>
              <span className={`text-xs font-bold ${c.color === "red" ? "text-red-400" : "text-white"}`}>
                {c.val}
              </span>
            </div>
          ))}
        </div>

        {/* ── QUICK ACTION CTA ──────────────────────────────────────────────── */}
        {/* Change button label or onClick to point to a real route            */}
        <div className="px-3 pb-4 flex-shrink-0">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
            Quick Action
          </p>
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2">
            <span className="text-base">+</span> New Order
          </button>
        </div>
      </aside>
    </>
  );
}
